import { useContext, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useGenericList } from '@app/hooks/useGenericList';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { makeCommaSeparatedString } from '@app/utils/stringUtils';

import { GenericList } from '@app/components/common/GenericList';
import { ASPECT_RATIO_TYPES } from '@app/components/common/ImageSelector/ImageSelector';
import { ColumnTypes, GenericObject, ORDER_TYPES } from '@app/components/common/Table/DefaultTable/types';

import { MediaFilter } from './MediaFilter';

const sortByDate = (media: GenericObject[] | null, key: string, order: string) => {
  if (!media) return [];

  const mediaClone = JSON.parse(JSON.stringify(media)) as GenericObject[];

  return mediaClone.sort((a, b) => {
    var dateA = new Date(a[key] as string);
    var dateB = new Date(b[key] as string);

    return order === ORDER_TYPES.ASCENDING ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
  });
};

export const ListMedia = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const notify = useNotify();
  const { isLoading, data, total, handleChangeFilters, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useGenericList('media', {
      customRowsPerPage: 100,
      filter: { categoryId: null, topicId: null, companyId: null, statusType: null, title: '' },
      sort: {
        field: 'date_created',
        order: 'DESC',
      },
    });
  const [order, setOrder] = useState<GenericObject>({ key: null, direction: null });

  const [media, setMedia] = useState<GenericObject[] | null>(null);

  const ability = useAbility();

  const columns = [
    {
      label: 'TITLE',
      key: 'title',
      aspectRatio: ASPECT_RATIO_TYPES.THUMBNAIL,
      getImagePath: (record: GenericObject) => `${(record?.vod_file_base as GenericObject)?.path}`,
      type: ColumnTypes.TITLE,
      format: (row: GenericObject) => `${getFieldOnMainLanguage(row?.title)}`,
    },
    {
      label: 'CREATED',
      key: 'date_created',
      type: ColumnTypes.DATE,
      sort: (order: string) => {
        setOrder({ key: 'date_created', direction: order });
      },
    },
    {
      label: 'PUBLISHED',
      key: 'publish_date',
      type: ColumnTypes.DATE,
      sort: (order: string) => {
        setOrder({ key: 'publish_date', direction: order });
      },
    },
    {
      label: 'CATEGORY',
      key: 'category',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.category),
    },
    {
      label: 'TOPIC',
      key: 'topic',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${getFieldOnMainLanguage((row?.taxonomy as GenericObject)?.category_name)}`,
    },
    {
      label: 'COMPANY',
      key: 'companies',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => {
        const companyNamesArray = (row?.companies as GenericObject[])?.map((company: GenericObject) =>
          getFieldOnMainLanguage(company.name)
        );

        return `${makeCommaSeparatedString(companyNamesArray)}`;
      },
    },
  ];

  if (ability.can(CASLAction.WRITE, 'media')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'status_type',
      type: ColumnTypes.STATUS,
      // @ts-ignore this is definitely valid
      validation: (row: GenericObject) => {
        if (row.status_type === 'INACTIVE' && (!row.taxonomy || (row?.taxonomy as GenericObject)?.id === 0)) {
          notify('Primary topic is required. Please update this media to include a Primary Topic before publishing.', {
            type: 'error',
          });
          return false;
        }
        return true;
      },
      publishedKey: 'status_type',
      isPublished: (row: GenericObject) => row.status_type === 'ACTIVE',
      getNewPublishedStatus: (row: GenericObject) => (row.status_type === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
    });
  }

  useEffect(() => {
    if (!data) {
      return;
    }

    let currentOrder = !order?.key ? { key: 'date_created', direction: ORDER_TYPES.DESCENDING } : order;
    setMedia(sortByDate(data, currentOrder.key as string, currentOrder.direction as string));
  }, [data, order]);

  if (!media) return null;

  return (
    <GenericList
      isListLoading={isLoading}
      rows={media}
      resourceSingular="media"
      generalFilterKey={'title'}
      generalFilterLabel="Search title"
      handleChangeLoading={handleChangeLoading}
      columns={columns}
      resource="media"
      title="Media"
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      total={total}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      noDefaultHeaderActions
      SearchFields={MediaFilter}
      handleChangeFilters={handleChangeFilters}
      rowsPerPageOptions={[100, 200, 300, 400]}
    />
  );
};
